import { useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";

const FORM_ENDPOINT = "https://forms.elido.nu/api/contact";

function getFormSiteId() {
  if (
    typeof window !== "undefined" &&
    window.location.hostname.endsWith(".pages.dev")
  ) {
    return "casagon-test";
  }
  return "casagon";
}

function createSubmissionId() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `casagon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

const SERVICE_OPTIONS = [
  "Golvläggning",
  "Renovering",
  "Kök",
  "Dörrar/fönster",
  "Till-/ombyggnation",
  "Annat",
];

export const QuoteForm = ({ dark = false }) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    location: "",
    message: "",
    website: "",
  });
  const [services, setServices] = useState([]);
  const [submissionId, setSubmissionId] = useState(() => createSubmissionId());
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const fieldClass = dark ? "field field-dark" : "field";
  const labelClass = `overline mb-2 block ${dark ? "text-warmwhite/50" : "text-graphite/50"}`;

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleService = (s) =>
    setServices((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );

  const submit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.phone || !form.email || !form.message) {
      toast.error("Fyll i namn, telefon, e-post och beskrivning.");
      return;
    }

    setSubmitting(true);

    try {
      const selectedServices =
        services.length > 0 ? services.join(", ") : "Ej valt";

      const fd = new FormData();
      fd.append("site_id", getFormSiteId());
      fd.append("submission_id", submissionId);
      fd.append("website", form.website || "");
      fd.append("subject", `Offertförfrågan – ${selectedServices}`);
      fd.append("name", form.name.trim());
      fd.append("phone", form.phone.trim());
      fd.append("email", form.email.trim());
      fd.append("service", services.join(","));
      if (form.location.trim()) fd.append("city", form.location.trim());
      fd.append("description", form.message.trim());
      fd.append(
        "message",
        [
          `Typ av arbete: ${selectedServices}`,
          form.location.trim() ? `Ort / område: ${form.location.trim()}` : "",
          "",
          form.message.trim(),
        ]
          .filter(Boolean)
          .join("\n"),
      );

      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        body: fd,
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        // The status check below handles non-JSON failures.
      }

      if (!response.ok || !data.ok || data.emailed !== true) {
        throw new Error(
          data.detail ||
            "Något gick fel. Försök igen eller ring oss.",
        );
      }

      setDone(true);
      setSubmissionId(createSubmissionId());
      toast.success("Tack! Din offertförfrågan är skickad.");
    } catch (err) {
      toast.error(
        err?.message || "Något gick fel. Försök igen eller ring oss.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="quote-success"
        className={`flex flex-col items-start gap-5 border p-10 ${
          dark
            ? "border-warmwhite/15 text-warmwhite"
            : "border-graphite/15 text-graphite"
        }`}
      >
        <span className="flex h-14 w-14 items-center justify-center bg-casared text-warmwhite">
          <Check size={26} />
        </span>
        <h3 className="font-display text-3xl font-bold tracking-tight">
          Tack för din förfrågan.
        </h3>
        <p
          className={
            dark ? "text-warmwhite/60" : "text-graphite/60"
          }
        >
          Vi återkommer så snart vi kan. Vill du hellre prata direkt är du
          varmt välkommen att ringa.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="quote-form" className="w-full">
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={update("website")}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <label className={labelClass}>Namn</label>
          <input
            data-testid="quote-name"
            className={fieldClass}
            value={form.name}
            onChange={update("name")}
            placeholder="Ditt namn"
            autoComplete="name"
          />
        </div>
        <div>
          <label className={labelClass}>Telefon</label>
          <input
            data-testid="quote-phone"
            className={fieldClass}
            value={form.phone}
            onChange={update("phone")}
            placeholder="07X-XXX XX XX"
            autoComplete="tel"
            inputMode="tel"
          />
        </div>
        <div>
          <label className={labelClass}>E-post</label>
          <input
            data-testid="quote-email"
            type="email"
            className={fieldClass}
            value={form.email}
            onChange={update("email")}
            placeholder="namn@exempel.se"
            autoComplete="email"
            inputMode="email"
          />
        </div>
        <div>
          <label className={labelClass}>Ort / område</label>
          <input
            data-testid="quote-location"
            className={fieldClass}
            value={form.location}
            onChange={update("location")}
            placeholder="T.ex. Lund eller Göteborg"
            autoComplete="address-level2"
          />
        </div>
      </div>

      <div className="mt-8">
        <label className={labelClass}>Typ av arbete</label>
        <div className="flex flex-wrap gap-3">
          {SERVICE_OPTIONS.map((s) => {
            const active = services.includes(s);
            return (
              <button
                type="button"
                key={s}
                data-testid={`quote-service-${s
                  .toLowerCase()
                  .replace(/[^a-zöäå]/g, "")}`}
                onClick={() => toggleService(s)}
                className={`flex items-center gap-2 border px-4 py-2.5 text-sm font-medium transition-colors duration-300 ${
                  active
                    ? "border-casared bg-casared text-warmwhite"
                    : dark
                      ? "border-warmwhite/25 text-warmwhite/80 hover:border-warmwhite"
                      : "border-graphite/25 text-graphite/80 hover:border-graphite"
                }`}
              >
                {active && <Check size={14} />}
                {s}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-8">
        <label className={labelClass}>Beskriv projektet</label>
        <textarea
          data-testid="quote-message"
          rows={5}
          className={`${fieldClass} resize-none`}
          value={form.message}
          onChange={update("message")}
          placeholder="Berätta kort om vad du vill ha hjälp med, omfattning och önskad tidsram."
        />
      </div>

      <button
        type="submit"
        data-testid="quote-submit"
        disabled={submitting}
        className={`group mt-8 flex w-full items-center justify-center gap-4 px-8 py-5 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 disabled:opacity-60 md:w-auto ${
          dark
            ? "bg-warmwhite text-graphite hover:bg-casared hover:text-warmwhite"
            : "bg-graphite text-warmwhite hover:bg-casared"
        }`}
      >
        {submitting ? "Skickar…" : "Skicka offertförfrågan"}
        {!submitting && (
          <span className="transition-transform duration-300 group-hover:translate-x-1">
            →
          </span>
        )}
      </button>
    </form>
  );
};
